import { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Cookie consent banner with GDPR toggles.
 *
 * Stored under localStorage["wc_cookie_consent_v1"] as:
 *   { necessary: true, analytics: bool, marketing: bool, set_at: ISO }
 *
 * Necessary is always true. Analytics/marketing default to OFF until
 * the user actively opts in (GDPR-compliant).
 *
 * Other components can read the consent via:
 *   window.localStorage.getItem("wc_cookie_consent_v1")
 * and gate behavioural tracking (Pixel, GA Analytics, Klaviyo etc.) on it.
 */
const KEY = "wc_cookie_consent_v1";

function loadConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConsent(c) {
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({ ...c, necessary: true, set_at: new Date().toISOString() })
    );
  } catch {
    /* ignore */
  }
  // Notify the rest of the app — useful if you later add Pixel/GA loaders
  // that hook into "wc:consent" events.
  try {
    window.dispatchEvent(new CustomEvent("wc:consent", { detail: c }));
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      // Show the banner only if no consent has been set yet
      setVisible(true);
    } else {
      setAnalytics(!!existing.analytics);
      setMarketing(!!existing.marketing);
    }

    // Allow other components to re-open the settings dialog
    const onOpen = () => {
      const cur = loadConsent() || {};
      setAnalytics(!!cur.analytics);
      setMarketing(!!cur.marketing);
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener("wc:open-cookie-settings", onOpen);
    return () => window.removeEventListener("wc:open-cookie-settings", onOpen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setVisible(false);
  };

  const savePrefs = () => {
    saveConsent({ analytics, marketing });
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[60] border-t-2 border-[#1A3626] bg-[#F9F9F4] shadow-[0_-12px_30px_rgba(0,0,0,0.08)]"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-[1400px] px-5 py-5 md:px-10 md:py-7">
        {!showSettings ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
            <div className="flex-1">
              <h2 className="font-display text-[11px] font-black uppercase tracking-[0.32em] text-[#1A3626] md:text-xs">
                Cookies & friends
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#1A3626]/85 md:text-[15px]">
                We use cookies to run the shop, remember your cart and quietly
                monitor which products people become emotionally attached to.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="font-display border-2 border-[#1A3626] px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.28em] text-[#1A3626] transition-all hover:bg-[#EBE7DD] md:px-5 md:text-xs"
                data-testid="cookie-settings"
              >
                Preferences
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="font-display border-2 border-[#1A3626] px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.28em] text-[#1A3626] transition-all hover:bg-[#EBE7DD] md:px-5 md:text-xs"
                data-testid="cookie-reject"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="font-display border-2 border-[#1A3626] bg-[#1A3626] px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.28em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] md:px-6 md:text-xs"
                data-testid="cookie-accept"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-display text-[11px] font-black uppercase tracking-[0.32em] text-[#1A3626] md:text-xs">
                Cookie preferences
              </h2>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                aria-label="Close"
                className="text-[#1A3626] transition-opacity hover:opacity-60"
                data-testid="cookie-close"
              >
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <Row
                title="Necessary"
                description="Required for the site to work — cart, checkout, login session. These cannot be switched off."
                checked
                disabled
              />
              <Row
                title="Analytics"
                description="Anonymised stats so we know which photos people stop scrolling on. No personal data sold."
                checked={analytics}
                onChange={() => setAnalytics((v) => !v)}
                testId="cookie-toggle-analytics"
              />
              <Row
                title="Marketing"
                description="Behavioural tracking so we can show you relevant ads on other sites. Off by default."
                checked={marketing}
                onChange={() => setMarketing((v) => !v)}
                testId="cookie-toggle-marketing"
              />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-2 md:gap-3">
              <button
                type="button"
                onClick={rejectAll}
                className="font-display border-2 border-[#1A3626] px-4 py-2.5 text-[10px] font-black uppercase tracking-[0.28em] text-[#1A3626] transition-all hover:bg-[#EBE7DD] md:px-5 md:text-xs"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="font-display border-2 border-[#1A3626] bg-[#1A3626] px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.28em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] md:px-6 md:text-xs"
                data-testid="cookie-save"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ title, description, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start gap-4 border border-[#1A3626]/20 bg-[#F9F9F4] p-4">
      <div className="flex-1">
        <div className="font-display text-[11px] font-black uppercase tracking-[0.28em] text-[#1A3626] md:text-xs">
          {title}
        </div>
        <p className="mt-1.5 text-sm leading-relaxed text-[#1A3626]/75">
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={onChange}
        data-testid={testId}
        className={`relative mt-1 h-7 w-12 shrink-0 border-2 border-[#1A3626] transition-colors ${
          checked ? "bg-[#1A3626]" : "bg-[#F9F9F4]"
        } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 border-2 border-[#1A3626] transition-all ${
            checked ? "left-[26px] bg-[#93C065]" : "left-0.5 bg-[#F9F9F4]"
          }`}
          aria-hidden
        />
      </button>
    </div>
  );
}

export { loadConsent };
